import { useEffect, useState } from 'react';
import {
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Box,
} from '@mui/material';
import theme from 'theme';
import { DarkTooltip } from '../../../components/DarkTooltip';
import { apiClient } from '../../../api-client';
import { useAuth } from '../../../auth';

const monthSx = {
  flexDirection: 'column',
  paddingRight: '0.125rem',
  paddingLeft: 0,
  paddingBottom: 0,
  paddingTop: 0,
};

const BillableDaysTile = () => {
  const { getToken } = useAuth();
  const [billableDaysForLastSevenMonths, setBillableDaysForLastSevenMonths] = useState([]);
  const [fetchFailed, setFetchFailed] = useState(false);
  const isLoaded = billableDaysForLastSevenMonths.length > 0;
  // current month excluded
  const billableDaysForLastSixMonths = billableDaysForLastSevenMonths.slice(0, -1);
  const numOfLoggedMonths = billableDaysForLastSixMonths.filter(item => item.isMonthLogged).length;
  const round = num => Math.round(num * 100) / 100;
  const averageBillableDaysForLastSixMonths =
    round(
      billableDaysForLastSixMonths.map(item => item.monthBillableDays).reduce((a, b) => a + b, 0) /
        numOfLoggedMonths,
    ) || 0;

  const billableDaysForCurrentMonth = billableDaysForLastSevenMonths.slice(-1)[0];
  const areLastSixMonthsBillable =
    billableDaysForLastSixMonths.filter(item => item.monthBillableDays > 0).length > 0;
  const areLastSixMonthsLogged =
    billableDaysForLastSixMonths.filter(item => item.isMonthLogged).length > 0;

  useEffect(() => {
    async function getBillableDaysForLastSevenMonths() {
      try {
        const url = '/tempo/billable-days';
        const response = await apiClient('GET', url, getToken);
        setBillableDaysForLastSevenMonths(response.billableDaysForLastSevenMonths);
      } catch (error) {
        console.error('Billable days fetch failed:', error);
        setFetchFailed(true);
      }
    }
    getBillableDaysForLastSevenMonths();
  }, [getToken]);

  const renderLastSixMonths = () => {
    const getStartAndEndMonth = () => {
      const startMonth = billableDaysForLastSixMonths[0].month.slice(0, 3);
      const endMonth = billableDaysForLastSixMonths.slice(-1)[0].month.slice(0, 3);

      return `${startMonth}-${endMonth}`;
    };

    if (!areLastSixMonthsLogged) {
      return (
        <ListItem sx={{ ...monthSx, backgroundColor: '#FEF6F4', textAlign: 'center' }}>
          <ListItemText
            sx={{
              '& span': {
                fontSize: '1.125rem',
                color: 'red',
                fontWeight: 500,
                cursor: 'pointer',
              },
            }}
          >
            0
          </ListItemText>
          <ListItemText
            sx={{
              '& span': {
                fontSize: '0.875rem',
                color: 'red',
                cursor: 'pointer',
              },
            }}
          >
            No calculations for last six months {getStartAndEndMonth()}
          </ListItemText>
        </ListItem>
      );
    }
    if (areLastSixMonthsBillable) {
      return billableDaysForLastSixMonths.map(item => (
        <DarkTooltip
          key={item.month}
          title={
            !item.isMonthLogged
              ? 'This month is not logged - not included in the average billable days'
              : ''
          }
        >
          <ListItem
            key={item.month}
            sx={[
              monthSx,
              !item.isMonthLogged && {
                '& span': {
                  color: 'red',
                  cursor: 'pointer',
                },
              },
            ]}
          >
            <ListItemText
              sx={[
                {
                  '& span': {
                    fontSize: '1.125rem',
                    color: '#000000',
                    fontWeight: 500,
                  },
                },
                !item.isMonthLogged && {
                  '& span': {
                    color: 'red',
                    cursor: 'pointer',
                  },
                },
              ]}
            >
              {item.monthBillableDays}
            </ListItemText>
            <ListItemText
              sx={[
                monthSx,
                !item.isMonthLogged && {
                  '& span': {
                    color: 'red',
                    cursor: 'pointer',
                  },
                },
              ]}
            >
              {item.month.slice(0, 3)}
            </ListItemText>
          </ListItem>
        </DarkTooltip>
      ));
    }
    return (
      <ListItem sx={monthSx}>
        <ListItemText
          sx={{
            '& span': {
              fontSize: '1.125rem',
              color: '#000000',
              fontWeight: 500,
            },
          }}
        >
          0
        </ListItemText>
        <ListItemText
          sx={{
            '& span': {
              fontSize: '1.125rem',
              color: '#000000',
              fontWeight: 500,
              textAlign: 'center',
            },
          }}
        >
          No billable days for last six months {getStartAndEndMonth()}
        </ListItemText>
      </ListItem>
    );
  };

  const renderCurrentMonth = () => (
    <DarkTooltip
      key={billableDaysForCurrentMonth.month}
      title={
        !billableDaysForCurrentMonth.isMonthLogged
          ? 'Current month is not logged - not counted in the 6-month average'
          : 'Current month - not counted in the 6-month average'
      }
    >
      <ListItem
        key={billableDaysForCurrentMonth.month}
        sx={[
          monthSx,
          { backgroundColor: '#F7F7F9', cursor: 'pointer' },
          !billableDaysForCurrentMonth.isMonthLogged && {
            backgroundColor: '#FEF6F4',
            textAlign: 'center',
          },
          (!areLastSixMonthsBillable || !areLastSixMonthsLogged) && { width: '5rem' },
        ]}
      >
        <ListItemText
          sx={[
            {
              '& span': {
                fontSize: '1.125rem',
                color: '#000000',
                fontWeight: 500,
              },
            },
            !billableDaysForCurrentMonth.isMonthLogged && {
              '& span': {
                color: 'red',
                cursor: 'pointer',
              },
            },
          ]}
        >
          {billableDaysForCurrentMonth.monthBillableDays}
        </ListItemText>
        <ListItemText
          sx={[
            {
              '& span': {
                fontSize: '0.875rem',
              },
            },
            !billableDaysForCurrentMonth.isMonthLogged && {
              '& span': {
                color: 'red',
                cursor: 'pointer',
              },
            },
          ]}
        >
          {billableDaysForCurrentMonth.month.slice(0, 3)}
        </ListItemText>
      </ListItem>
    </DarkTooltip>
  );

  const renderAverageBillableDays = () => {
    if (isLoaded) {
      return (
        <Typography
          sx={{ color: '#000000', fontSize: '1.125rem', fontWeight: 500, textAlign: 'end' }}
          variant="body1"
        >
          {averageBillableDaysForLastSixMonths} days
        </Typography>
      );
    }
    if (!fetchFailed) {
      return <LinearProgress sx={{ width: theme.spacing(8) }} />;
    }
    return null;
  };

  const renderBillableDays = () => {
    if (isLoaded) {
      return (
        <List sx={{ display: 'flex', flexDirection: 'row', paddingTop: '1rem' }}>
          {renderLastSixMonths()}
          <Box
            component="div"
            sx={{
              borderLeft: `1px solid ${theme.palette.border}`,
              marginRight: '0.563rem',
              marginLeft: '0.563rem',
              '@media (max-width:480px)': {
                marginRight: '0.25rem',
                marginLeft: '0.25rem',
              },
            }}
          />
          {renderCurrentMonth()}
        </List>
      );
    }
    if (fetchFailed) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            marginTop: theme.spacing(2),
            textAlign: 'center',
          }}
        >
          Unable to load billable days.
        </Typography>
      );
    }
    return (
      <>
        <LinearProgress sx={{ marginBottom: theme.spacing(2), marginTop: theme.spacing(4) }} />
        <LinearProgress sx={{ marginBottom: theme.spacing(2), marginTop: theme.spacing(4) }} />
      </>
    );
  };

  return (
    <Paper sx={{ padding: '1.125rem 1.5rem', borderRadius: '0.25rem' }} elevation={1}>
      <Box
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: '0.75rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <div>
          <Typography
            sx={{ color: '#000000', fontSize: '1.125rem', fontWeight: 500 }}
            variant="body1"
          >
            Billable days
          </Typography>
          <Typography variant="body2">Average for the last 6 months</Typography>
        </div>
        {renderAverageBillableDays()}
      </Box>
      {renderBillableDays()}
    </Paper>
  );
};

export default BillableDaysTile;
